import useFetcher from './useFetcher';
import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { baseTenantsApiUrlV3, baseApiUrl } from '@utils/api';
import { useUser } from 'app/_providers/User';

import { useCallback } from 'react';
import {
  MemberAccessCheckRequestItem,
  MemberAccessCheckResponse,
  MemberAccessCheckType,
} from 'shared/src/generated/openapi/v3/types';

export class Permissions {
  constructor(
    private response: MemberAccessCheckResponse = {},
    private systemResponse: Record<string, boolean> = {},
  ) {}

  public hasOrgPermission(...permissionChecks: string[]): boolean {
    const orgPermissions = this.response.organization;

    if (!orgPermissions) {
      return false;
    }

    return permissionChecks.every((permission) => orgPermissions[permission]);
  }

  public hasWorkspacePermission(workspaceId: string, ...permissionChecks: string[]): boolean {
    if (!this.response.workspaces) {
      return false;
    }

    const wsPermissions = this.response.workspaces[workspaceId];

    if (!wsPermissions) {
      return false;
    }

    return permissionChecks.every((permission) => wsPermissions[permission]);
  }

  public hasProductPermission(workspaceId: string, product: string, ...permissionChecks: string[]): boolean {
    if (!this.response.workspaces) {
      return false;
    }

    const wsPermissions = this.response.workspaces[workspaceId];

    if (!wsPermissions?.products) {
      return false;
    }

    const productPermissions = wsPermissions.products[product];

    if (!productPermissions) {
      return false;
    }

    return permissionChecks.every((permission) => productPermissions[permission]);
  }

  public hasSystemPermission(...permissionChecks: string[]): boolean {
    if (!this.systemResponse) {
      return false;
    }

    return permissionChecks.every((permission) => this.systemResponse[permission] || false);
  }
}

export const NoPermissions = new Permissions();

type AccessHook = () => {
  loading: boolean;
  userId: string;
  organizationId: string | null;
  checkPermissions: (...accessChecks: MemberAccessCheckRequestItem[]) => Promise<Permissions>;
};

const useAccess: AccessHook = () => {
  const user = useUser();
  const { authorizedOrganizationId: organizationId } = useAuthProvider();
  const { fetcher } = useFetcher();

  const checkPermissions = useCallback(
    async (...accessChecks: MemberAccessCheckRequestItem[]): Promise<Permissions> => {
      if (!user?.sub) {
        return new Permissions();
      }

      // Separate system permissions from organization-scoped permissions
      const systemChecks = accessChecks.filter((check) => check.accessType === MemberAccessCheckType.System);
      const orgChecks = accessChecks.filter((check) => check.accessType !== MemberAccessCheckType.System);

      let orgResponse: MemberAccessCheckResponse = {};
      let systemResponse: Record<string, boolean> = {};

      try {
        // Handle organization-scoped permissions (requires organizationId)
        if (orgChecks.length > 0) {
          if (!organizationId) {
            throw new Error('Organization ID required for non-system permissions');
          }

          orgResponse = await fetcher<MemberAccessCheckResponse>(
            `${baseTenantsApiUrlV3}/${organizationId}/members/self/access`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(orgChecks),
            },
          );
        }

        // Handle system permissions (no organizationId required)
        if (systemChecks.length > 0) {
          const systemChecksPayload = systemChecks.flatMap((check) => check.checks || []);

          const systemApiResponse = await fetcher<{ checks: Record<string, boolean> }>(
            `${baseApiUrl}/v2/system/access`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ checks: systemChecksPayload }),
            },
          );

          systemResponse = systemApiResponse.checks || {};
        }

        return new Permissions(orgResponse, systemResponse);
      } catch (err: any) {
        if (String(err).includes('Unauthorized')) {
        /*
          user must have can_read_<?????> to access the ?????? page first
          so a caught error here is an unauthorized error not a breaking error
        */
          return new Permissions();
        }
        throw err;
      }
    }, [user.sub, organizationId]);

  return {
    loading: user.loading,
    userId: user?.sub,
    organizationId,
    checkPermissions,
  };
};

export default useAccess;
